import { useCallback, useState } from "react";
import { Formik, validateYupSchema, yupToFormErrors } from "formik";
import { FormikConfig, FormikErrors, FormikValues } from "formik/dist/types";
import { FieldsState, FieldsStateContext } from "./validation/FieldsStateContext";
import { FieldState, InputState } from "./types";

export type MimicFormProps<Values extends FormikValues = FormikValues, ExtraProps = {}, VCP extends object = {}> =
  FormikConfig<Values> & ExtraProps & { validationContextProps?: VCP };

export type ValidateWithStateUpdatesSignature = (
  validateFunc: () => boolean | Promise<boolean>,
  path: string,
  options?: {
    progressMessage?: string,
    successMessage?: string,
  },
) => Promise<boolean>

export type YupValidationContext<Values extends FormikValues = FormikValues, VCP extends object = {}> = VCP & {
  values?: Values,
  initialValues?: Values,
  validateWithStateUpdates?: ValidateWithStateUpdatesSignature,
};

export function MimicFormik<Values extends FormikValues = FormikValues, ExtraProps = {}, VCP extends object = {}>(
  {
    validationSchema,
    validate,
    initialValues,
    validationContextProps,
    ...props
  }: MimicFormProps<Values, ExtraProps, VCP>): JSX.Element {
  const [ fieldsState, setFieldsState ] = useState<FieldsState>({});

  const validateWithStateUpdates: ValidateWithStateUpdatesSignature = useCallback(async function (
    validateFunc: () => boolean | Promise<boolean>,
    path: string,
    options?: {
      progressMessage?: string,
      successMessage?: string,
    },
  ) {
    const updateFieldState = (inputState: FieldState) => setFieldsState((state) => ({
      ...state,
      [path]: inputState,
    }));
    try {
      updateFieldState({
        state: InputState.progress,
        stateText: options?.progressMessage ?? "Validation in progress",
      });
      const res = await validateFunc();
      updateFieldState({
        state: InputState.success,
        stateText: options?.successMessage ?? "The field is valid",
      });
      return res;
    } catch (e: any) {
      updateFieldState({
        state: InputState.error,
      });
      return false;
    }
  }, [ setFieldsState ]);

  const validateFunc = useCallback(async function (values: Values): Promise<FormikErrors<Values>> {
    try {
      await validateYupSchema(values, validationSchema, false, {
        values,
        initialValues,
        validateWithStateUpdates,
        ...validationContextProps,
      } as YupValidationContext<Values, VCP>);
    } catch (err) {
      return yupToFormErrors(err);
    }
    return {};
  }, [
    validationSchema, initialValues, validationContextProps, validateWithStateUpdates,
  ]);

  return (
    <FieldsStateContext.Provider value={fieldsState}>
      <Formik
        validate={validateFunc}
        initialValues={initialValues}
        {...props}
      />
    </FieldsStateContext.Provider>
  );
}